import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router';
import { useCaseId } from '@/stores/caseStore';
import { pathwayActions } from '@/stores/pathwayStore';
import { settingsActions } from '@/stores/settingsStore';
import featureConfig from '@/featureConfig';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getRoundedPath } from '@/utils/svgUtils';
import PortTooltip from '@/components/nodes/PortTooltip';
import { buildHandle } from '@/utils/pathwayUtils';

/**
 * LimiterNode Component
 * Renders a compact, triangular node for Stream Limiter (same shape as Combiner).
 *
 * - Uses the same right-pointing triangle as Combiner with a valve icon superimposed
 * - Inputs on left base (primary, secondary) and output on right tip
 * - The node size scales dynamically based on the number of ports to ensure
 *   connectors are evenly spaced.
 */
const LimiterNode = ({ data: node }) => {
  const { id, inputs, outputs, label } = node;
  const { projectId, pathwayId, nodeId } = useParams();
  const caseId = useCaseId();
  const navigate = useNavigate();
  const updateNodeInternals = useUpdateNodeInternals();

  // Dynamic Sizing Logic:
  // We use the same equilateral triangle geometry as ArrowNode (Combiner shape)
  // 1. Calculate height based on the number of ports (ensuring enough vertical space).
  const maxPorts = Math.max(inputs?.length || 0, outputs?.length || 0);
  const portSpacing = 100;
  const minHeight = 50;
  const height = Math.max(minHeight, (maxPorts + 1) * portSpacing * 0.35);

  // 2. Calculate width to maintain equilateral proportions (altitude = side * sqrt(3)/2)
  const width = height * (Math.sqrt(3) / 2);

  // Force ReactFlow to update internal handle positions when dimensions change
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, width, height, updateNodeInternals]);

  const removeNode = e => {
    e.stopPropagation();
    pathwayActions.removeNode(id);
    if (nodeId === id) {
      navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`);
    }
  };

  const handleNodeClick = () => {
    settingsActions.closePalette();
    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}/nodes/${id}`, { state: { node } });
  };

  // Generate the SVG path for the rounded triangle (same as Combiner: right-pointing)
  const pathD = useMemo(() => {
    const radius = 8; // Rounded corner radius
    const points = [
      [0, 0],
      [width, height / 2],
      [0, height],
    ]; // Right-pointing triangle vertices
    return getRoundedPath(points, radius);
  }, [width, height]);

  return (
    <div className="relative group/item cursor-pointer" style={{ width, height }} onClick={handleNodeClick}>
      {/* Label - Floating above the triangle */}
      <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 flex justify-center pointer-events-none">
        <span className="text-base font-bold text-gray-700 truncate max-w-[300px] text-center bg-white/50 px-1 rounded">
          {label}
        </span>
      </div>

      {/* Delete Button - Positioned at the top vertex of the triangle */}
      {featureConfig.manageModules && (
        <div
          className="absolute z-20 invisible group-hover/item:visible"
          style={{
            top: -12,
            left: -12,
          }}
        >
          <div
            onClick={removeNode}
            className="p-1 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </div>
        </div>
      )}

      {/* The Shape Layer */}
      <svg
        className="absolute inset-0 w-full h-full drop-shadow-md transition-all duration-200 hover:drop-shadow-lg"
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: 'visible' }}
      >
        <path
          d={pathD}
          fill="white"
          stroke="#374151"
          strokeWidth="1"
          strokeLinejoin="round"
          className="transition-colors duration-200 group-hover/item:stroke-gray-900"
        />
        {/* Valve icon - circle with handle, centered on triangle, rotated 45 degrees */}
        <g transform={`translate(${width * 0.4}, ${height / 2}) rotate(45)`}>
          {/* Circle */}
          <circle cx="0" cy="0" r={Math.min(width * 0.15, height * 0.2)} fill="none" stroke="#374151" strokeWidth="2" />
          {/* Handle/line through center */}
          <line
            x1={-Math.min(width * 0.15, height * 0.2)}
            y1="0"
            x2={Math.min(width * 0.15, height * 0.2)}
            y2="0"
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
          />
          {/* Vertical line */}
          <line
            x1="0"
            y1={-Math.min(width * 0.15, height * 0.2)}
            x2="0"
            y2={Math.min(width * 0.15, height * 0.2)}
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </g>
      </svg>

      {/* Inputs Layer - Evenly spaced vertically on left base (same as Combiner) */}
      <div className="absolute top-0 bottom-0 left-0 flex flex-col justify-evenly items-start" style={{ width: '30%' }}>
        {inputs?.map(input => (
          <div key={input.id} className="relative group/handle flex items-center">
            <Handle
              type="target"
              position={Position.Left}
              id={buildHandle(id, 'input', input.name)}
              className="w-4 h-4 !bg-gray-200 !border-2 !border-black hover:!bg-gray-100 transition-colors"
            />
            <PortTooltip nodeId={id} ioName={input.name} ioLabel={input.label} handleType="input" position="left" />
          </div>
        ))}
      </div>

      {/* Outputs Layer - On right tip (same as Combiner) */}
      <div className="absolute top-0 bottom-0 right-0 flex flex-col justify-center items-end" style={{ width: '20%' }}>
        {outputs?.map(output => (
          <div key={output.id} className="relative group/handle flex items-center justify-end">
            <Handle
              type="source"
              position={Position.Right}
              id={buildHandle(id, 'output', output.name)}
              className="w-4 h-4 !bg-gray-200 !border-2 !border-black hover:!bg-gray-100 transition-colors"
            />
            <PortTooltip nodeId={id} ioName={output.name} ioLabel={output.label} handleType="output" position="right" />
          </div>
        ))}
      </div>
    </div>
  );
};

LimiterNode.propTypes = {
  data: PropTypes.object,
};

const MemoizedLimiterNode = memo(LimiterNode);

export default MemoizedLimiterNode;
